<template>
  <div class="imoveis-page">
    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Todos os Imóveis</div>
          <div class="page-subtitle">
            Visão geral de todos os anúncios publicados
          </div>
        </div>
        <q-btn
          class="gt-sm publish-btn"
          label="Publicar Imóvel"
          icon="add_circle"
          unelevated
          to="/imovel-steped/adicionar"
        />
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">
      <!-- Loading skeletons -->
      <div v-if="loading" class="row q-col-gutter-lg">
        <div v-for="n in 8" :key="n" class="col-12 col-sm-6 col-md-3">
          <div class="skeleton-card">
            <q-skeleton height="180px" class="skeleton-img" />
            <div class="q-pa-md">
              <q-skeleton type="text" width="60%" class="q-mb-sm" />
              <q-skeleton type="text" width="90%" class="q-mb-xs" />
              <q-skeleton type="text" width="75%" />
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="imoveis.length === 0" class="empty-state">
        <div class="empty-icon-wrap">
          <q-icon name="home_work" size="48px" color="grey-4" />
        </div>
        <div class="empty-title">Nenhum imóvel encontrado</div>
        <div class="empty-sub">Ainda não existem imóveis publicados.</div>
      </div>

      <!-- Grid -->
      <q-infinite-scroll v-else @load="onLoad" :offset="250">
        <div class="row q-col-gutter-lg">
          <div
            v-for="(imovel, index) in imoveis"
            :key="index"
            class="col-12 col-sm-6 col-md-3"
          >
            <q-card
              class="imovel-card"
              :class="{ 'card-featured': imovel.is_featured }"
              flat
            >
              <!-- Image -->
              <div class="image-container">
                <q-badge
                  class="tipo-negocio-badge"
                  :class="
                    imovel.objective === 'Venda'
                      ? 'badge-venda'
                      : 'badge-arrendamento'
                  "
                >
                  {{ imovel.objective }}
                </q-badge>
                <div
                  class="featured-badge"
                  :class="
                    imovel.is_featured ? 'badge-active' : 'badge-inactive'
                  "
                >
                  <q-icon
                    :name="imovel.is_featured ? 'check_circle' : 'cancel'"
                    size="12px"
                  />
                  {{ imovel.is_featured ? "Activo" : "Inactivo" }}
                </div>
                <q-img
                  :src="imovel.primary_image_url"
                  :ratio="16 / 10"
                  class="cursor-pointer card-img"
                  @click="verDetalhes(imovel)"
                >
                  <template v-slot:loading>
                    <q-spinner-gears color="white" />
                  </template>
                </q-img>
              </div>

              <!-- Body -->
              <q-card-section class="card-body">
                <div class="property-type">{{ imovel.property_type }}</div>
                <div class="property-title ellipsis-2-lines">
                  {{ imovel.title }}
                </div>

                <div class="location-row">
                  <q-icon name="location_on" size="14px" />
                  <span>{{ imovel.location }}</span>
                </div>

                <!-- Stats row -->
                <div class="stats-row">
                  <div class="stat">
                    <q-icon name="bed" size="16px" />
                    <span>{{ imovel.bedroom_amount }}</span>
                  </div>
                  <div class="stat">
                    <q-icon name="bathtub" size="16px" />
                    <span>{{ imovel.bathroom_amount }}</span>
                  </div>
                  <div class="stat">
                    <q-icon name="square_foot" size="16px" />
                    <span>{{ imovel.area }}m²</span>
                  </div>
                  <div class="stat">
                    <q-icon name="garage" size="16px" />
                    <span>{{ imovel.garage_space }}</span>
                  </div>
                  <div class="stat">
                    <q-icon name="visibility" size="16px" />
                    <span>{{ imovel.clicks }}</span>
                  </div>
                </div>

                <div class="card-divider" />

                <!-- Price + action -->
                <div class="price-row">
                  <div class="price">
                    {{ formatPrice(imovel.price, imovel.currency) }}
                  </div>
                  <div class="action-btns">
                    <button
                      class="star-btn"
                      :class="{ 'star-active': imovel.is_featured }"
                      :title="
                        imovel.is_featured
                          ? 'Remover destaque'
                          : 'Destacar imóvel'
                      "
                      @click.stop="toggleFeatured(imovel)"
                    >
                      <q-icon
                        :name="imovel.is_featured ? 'star' : 'star_border'"
                        size="18px"
                      />
                    </button>
                    <button
                      v-if="canEdit(imovel)"
                      class="edit-btn"
                      title="Editar imóvel"
                      @click.stop="editarImovel(imovel)"
                    >
                      <q-icon name="edit" size="16px" />
                    </button>
                    <button class="detail-btn" @click="verDetalhes(imovel)">
                      <q-icon name="arrow_forward" size="18px" />
                    </button>
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <template v-slot:loading>
          <div class="row justify-center q-my-lg">
            <q-spinner-dots color="primary" size="36px" />
          </div>
        </template>
      </q-infinite-scroll>

      <!-- Pagination -->
      <div v-if="!loading && totalPages > 1" class="pagination-row">
        <q-pagination
          v-model="currentPage"
          :max="totalPages"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          active-color="primary"
          active-text-color="white"
          @update:model-value="getProperties(currentPage)"
        />
      </div>
    </div>

    <!-- FAB mobile -->
    <q-page-sticky position="bottom-right" :offset="[24, 24]" class="lt-md">
      <q-btn
        fab
        color="secondary"
        icon="add"
        size="xl"
        to="/imovel-steped/adicionar"
      />
    </q-page-sticky>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import store from "@/vuex/store";
import router from "@/router";
const currentPage = ref(1);
const totalPages = ref(1);
let loading = ref(true);

const imoveis = ref([]);
onMounted(() => {
  getProperties();
});

function getProperties(page = 1) {
  loading.value = true;
  imoveis.value = [];
  store.dispatch("imovel/getAllImovel", page).then(() => {
    imoveis.value = store.state.imovel.data.data;
    totalPages.value = store.state.imovel.data.last_page;
    loading.value = false;
  });
}

const formatPrice = (price, currency) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(price);
};

const verDetalhes = (imovel) => {
  router.push({ name: "imovel-details", params: { slug: imovel.slug } });
};

const canEdit = (imovel) => {
  const user = store.state.auth.user;
  if (!user) return false;

  return (
    user.role === 1 || user.id === imovel.user_id || user.id === imovel.user?.id
  );
};

const editarImovel = (imovel) => {
  if (!imovel.slug) return;
  router.push({ name: "edit-imovel-steped", params: { slug: imovel.slug } });
};

const onLoad = (_index, done) => {
  setTimeout(() => {
    done();
  }, 500);
};

const toggleFeatured = (imovel) => {
  store
    .dispatch("imovel/toggleFeatured", imovel.slug)
    .then((res) => {
      imovel.is_featured = res.data.is_featured;
      Swal.fire({
        icon: res.data.is_featured ? "success" : "info",
        title: res.message,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
    })
    .catch(() => {
      Swal.fire({
        icon: "error",
        title: "Erro ao actualizar destaque.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2500,
      });
    });
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.imoveis-page {
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── Header ───────────────────────────────────────────── */
.page-header {
  background: #0d1b2a;
  padding: 20px 24px;
}
.page-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  gap: 12px;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}
.page-subtitle {
  font-size: 12px;
  color: rgba(212, 175, 55, 0.75);
  letter-spacing: 0.4px;
  margin-top: 2px;
}
.publish-btn {
  background: #d4af37 !important;
  color: #0d1b2a !important;
  font-weight: 700;
  border-radius: 10px;
  font-size: 13px;
  letter-spacing: 0.3px;
}

/* ── Content ──────────────────────────────────────────── */
.content-wrapper {
  max-width: 1400px;
  margin: 28px auto;
  padding: 0 20px 48px;
}

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e8ecf4;
}
.skeleton-img {
  border-radius: 0;
}

/* ── Empty state ──────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 16px;
  text-align: center;
}
.empty-icon-wrap {
  width: 88px;
  height: 88px;
  background: #fff;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ecf4;
  box-shadow: 0 4px 16px rgba(13, 27, 42, 0.06);
  margin-bottom: 20px;
}
.empty-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.empty-sub {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 6px;
}

/* ── Card ─────────────────────────────────────────────── */
.imovel-card {
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e8ecf4;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.imovel-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(13, 27, 42, 0.12);
}

/* ── Image ────────────────────────────────────────────── */
.image-container {
  position: relative;
  overflow: hidden;
}
.card-img {
  transition: transform 0.35s ease;
  display: block;
}
.imovel-card:hover .card-img {
  transform: scale(1.05);
}

/* ── Objective badge ──────────────────────────────────── */
.tipo-negocio-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  letter-spacing: 0.4px;
}
.badge-venda {
  background: rgba(22, 163, 74, 0.92) !important;
  color: #fff !important;
}
.badge-arrendamento {
  background: rgba(37, 99, 235, 0.92) !important;
  color: #fff !important;
}

/* ── Card body ────────────────────────────────────────── */
.card-body {
  padding: 16px !important;
  position: relative;
  z-index: 2;
}
.property-type {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #d4af37;
  margin-bottom: 4px;
}
.property-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  margin-bottom: 8px;
}
.ellipsis-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 38px;
}
.location-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
}
.location-row .q-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

/* ── Stats ────────────────────────────────────────────── */
.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.stat .q-icon {
  color: #94a3b8;
}

/* ── Divider ──────────────────────────────────────────── */
.card-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 12px 0;
}

/* ── Featured ─────────────────────────────────────────── */
.card-featured {
  border-color: #d4af37 !important;
  box-shadow: 0 0 0 1px #d4af37;
}
.featured-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  padding: 3px 9px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 3px;
}
.badge-active {
  background: rgba(22, 163, 74, 0.92);
  color: #fff;
}
.badge-inactive {
  background: rgba(13, 27, 42, 0.6);
  color: rgba(255, 255, 255, 0.85);
}

/* ── Price row ────────────────────────────────────────── */
.price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.price {
  font-size: 17px;
  font-weight: 800;
  color: #0d1b2a;
  letter-spacing: -0.3px;
}
.action-btns {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.star-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #94a3b8;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s;
}
.star-btn:hover {
  background: #fef9ec;
  color: #d4af37;
}
.star-active {
  background: #fef9ec !important;
  color: #d4af37 !important;
}
.edit-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s;
}
.edit-btn:hover {
  background: #e0f2fe;
  color: #0369a1;
}
.detail-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #0d1b2a;
  color: #d4af37;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s;
  flex-shrink: 0;
}
.detail-btn:hover {
  background: #162333;
}

/* ── Pagination ───────────────────────────────────────── */
.pagination-row {
  display: flex;
  justify-content: center;
  padding-top: 36px;
}
</style>
